import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import styled from "styled-components";
import Draggable from "react-draggable";
import Modal from "react-modal";
import { HexColorPicker } from "react-colorful";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import ResizeObserver from "resize-observer-polyfill";
// Styled Components
const TableNode = styled.div`
  position: absolute;
  width: ${(props) => `${props.width}px`};
  height: ${(props) => `${props.height}px`};
  left: ${(props) => `${props.x}px`};
  top: ${(props) => `${props.y}px`};
  border: 2px solid black;
  border-radius: 50px;
  background-color: ${(props) =>
    props.isAvailable ? props.color : "rgba(128, 128, 128, 0.5)"};
  text-align: center;
  cursor: ${(props) => (props.isAvailable ? "pointer" : "not-allowed")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => `${props.fontSize}px`};
  z-index: 0;
  opacity: ${(props) => (props.isAvailable ? "1" : "0.7")};
  transition:
    left 0.5s ease,
    top 0.5s ease,
    width 0.5s ease,
    height 0.5s ease;

  ${(props) =>
    !props.isAvailable &&
    `
    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 2px;
      background-color: black;
      transform: rotate(-45deg);
    }
  `}
`;

const IDContainer = styled.div`
  width: ${(props) => `${props.fontSize * 2}px`};
  height: ${(props) => `${props.fontSize * 2}px`};
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => `${props.fontSize}px`};
  color: black;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 10px 15px;
  border-radius: 10px;
  background-color: #f5f5f5;
  border: none;
  font-size: 16px;
  font-weight: bold;
  color: #2c3e50;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='10'%3E%3Cpath fill='%232c3e50' d='M7 10L0 0h14z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: calc(100% - 10px) center;
  background-size: 10px;
  &:focus {
    outline: none;
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
  }
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 10px 15px;
  border-radius: 10px;
  background-color: #f5f5f5;
  border: none;
  font-size: 16px;
  color: #2c3e50;
  margin-bottom: 15px;
  &:focus {
    outline: none;
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
  }
`;

const StyledH4 = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 10px;
`;

// Main Component
function StaticTables({
  imageUrl,
  initialTables = [],
  onTableDataChange,
  eventId,
  selectedTab,
  ind,
  handleTableClick
}) {
  const [imageSize, setImageSize] = useState([null, null]);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const imageContainerRef = useRef(null); // Use ref instead of ID
  const [tableForm, setTableForm] = useState({
    pricingType: "universal",
    price: "",
    pricesPerDay: Array(7).fill(""),
    people: "",
    color: "#000000",
    name: "",
    count: 1
  });
  const [existingCategories, setExistingCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("new");
  const [zoomLevel, setZoomLevel] = useState(1);

  // Handle
  const handleImageLoad = (event) => {
    const { width, height } = event.target;
    setImageSize({ width, height });
    setImageLoaded(true);
  };

  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    const handleResize = () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;

      if (newWidth > 0 && newHeight > 0) {
        setImageSize({ width: newWidth, height: newHeight });
        setTables((prevTables) =>
          prevTables.map((table) => ({
            ...table,
            x: (table.xPercentage / 100) * newWidth,
            y: (table.yPercentage / 100) * newHeight,
            width: (table.widthPercentage / 100) * newWidth,
            height: (table.heightPercentage / 100) * newHeight
          }))
        );
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    resizeObserver.observe(container);

    // Call handleResize initially
    handleResize();

    return () => {
      resizeObserver.unobserve(container);
    };
  }, [imageLoaded, imageUrl, initialTables]);

  useLayoutEffect(() => {
    if (
      imageSize.width > 0 &&
      imageSize.height > 0 &&
      initialTables?.length > 0
    ) {
      const updatedTables = initialTables.map((table) => ({
        ...table,
        x: (table.xPercentage / 100) * imageSize.width,
        y: (table.yPercentage / 100) * imageSize.height,
        width: (table.widthPercentage / 100) * imageSize.width,
        height: (table.heightPercentage / 100) * imageSize.height
      }));
      setTables(updatedTables);
    }
  }, [initialTables, imageSize]);

  // Handle Table Form Submission
  const saveBookingDetails = () => {
    const updatedTables = tables.map((table) =>
      table.id === selectedTable.id ? selectedTable : table
    );
    onTableDataChange(updatedTables);
    setTables(updatedTables);
    setIsOpen(false);
  };

  return (
    <div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setIsOpen(false)}
        style={{
          content: {
            top: "50%",
            left: windowWidth > 900 ? "60%" : "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#F0F0F0",
            borderRadius: "2%",
            width: windowWidth < 700 ? "80%" : "60%",
            height: "60%"
          },
          overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)" }
        }}
        contentLabel="Table Information"
      >
        {selectedTable && (
          <div>
            <h3>Table Information</h3>
            <p>Table Name: {selectedTable.tableName}</p>
            <p>Seats: {selectedTable.seats}</p>
            <StyledH4>Booking Details</StyledH4>
            <StyledInput
              type="text"
              name="namereservation"
              placeholder="Name"
              value={selectedTable.bookingDetails?.namereservation || ""}
              onChange={(e) =>
                setSelectedTable((prevTable) => ({
                  ...prevTable,
                  bookingDetails: {
                    ...prevTable.bookingDetails,
                    namereservation: e.target.value
                  }
                }))
              }
            />
            <StyledInput
              type="email"
              name="emailreservation"
              placeholder="Email"
              value={selectedTable.bookingDetails?.emailreservation || ""}
              onChange={(e) =>
                setSelectedTable((prevTable) => ({
                  ...prevTable,
                  bookingDetails: {
                    ...prevTable.bookingDetails,
                    emailreservation: e.target.value
                  }
                }))
              }
            />
            <StyledSelect
              name="paymentStatus"
              value={selectedTable.bookingDetails?.paymentStatus || "Available"}
              onChange={(e) =>
                setSelectedTable((prevTable) => ({
                  ...prevTable,
                  bookingDetails: {
                    ...prevTable.bookingDetails,
                    paymentStatus: e.target.value
                  }
                }))
              }
            >
              <option value="Available">Available</option>
              <option value="Pending">Pending</option>
              <option value="Completed">Completed</option>
            </StyledSelect>
            <button
              style={{
                marginTop: "20px",
                padding: "10px 10px",
                borderRadius: "20px",
                fontSize: "10px",
                fontWeight: "bold",
                background:
                  "linear-gradient(to top right, rgba(91, 91, 91, 1), rgba(28, 57, 150, 1))",
                color: "white",
                border: "none",
                cursor: "pointer"
              }}
              onClick={saveBookingDetails}
            >
              Save Booking Information
            </button>
          </div>
        )}
      </Modal>

      {selectedTable && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "20px",
            justifyContent: "flex-start"
          }}
        >
          <div
            style={{
              backgroundColor: "#F0F0F0",
              borderRadius: "10px",
              padding: "20px",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)"
            }}
          >
            <h3>Selected Table Info {selectedTable.id}</h3>
            <p>ID: {selectedTable.id}</p>
            <p>Price: €{Math.min(...selectedTable.pricesPerDay)}</p>
            <p>People: {selectedTable.people}</p>
            <p>Name: {selectedTable.name}</p>
          </div>
        </div>
      )}

      <div
        ref={imageContainerRef} // Attach ref here
        style={{
          width: "100%",
          paddingBottom: "100%",
          position: "relative",
          overflow: "hidden",
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          borderRadius: "20px",
          backgroundColor: "#F0F0F0",
          border: "1px solid #F0F0F0"
        }}
      >
        <img
          src={`${imageUrl}?selectedTab=${selectedTab}`}
          alt="table-layout"
          style={{ display: "none" }}
          onLoad={handleImageLoad}
        />

        {tables.map((table) => {
          const fontSize = table.width / 3;
          return (
            <TableNode
              key={table.id}
              color={table.color}
              width={table.width}
              height={table.height}
              fontSize={fontSize}
              isAvailable={table.isAvailable}
              style={{ left: table.x, top: table.y }}
              onClick={() => {
                handleTableClick(table);
              }}
            >
              <IDContainer fontSize={fontSize}>{table.id}</IDContainer>
            </TableNode>
          );
        })}
      </div>
    </div>
  );
}

export default StaticTables;
